import { h } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import Storefront from 'utils/storefront';
import * as queries from 'utils/storefront/queries';
import ProductCard from 'utils/product-card';
import { edgesToNodes } from 'utils/storefront/format-resource';

const ProductGrid = (props) => {
  const storefront = Storefront.getInstance();
  const [products, setProducts] = useState(props.collection.products || []);
  const [isDesktop, setIsDesktop] = useState(props.layout?.bps.above.lg.matches);

  const [bundleBuilderSelectedProducts, setBundleBuilderSelectedProducts] = useState([]);
  const [bundleItemAdded, setBundleItemAdded] = useState({id: 0, multi: null});
  const [bundleItemRemoved, setBundleItemRemoved] = useState({id: 0, multi: null});

  if (bundleItemAdded?.id) {
    props.updateTotalCount("add", bundleItemAdded);
  }
  if (bundleItemRemoved?.id) {
    props.updateTotalCount("minus", bundleItemRemoved);
  }

  // refs
  const bundleBuilderSelectedProductsRef = useRef(bundleBuilderSelectedProducts);
  bundleBuilderSelectedProductsRef.current = bundleBuilderSelectedProducts;
  const bundleItemRemovedRef = useRef(bundleItemRemoved);
  bundleItemRemovedRef.current = bundleItemRemoved;
  const bundleItemAddedRef = useRef(bundleItemAdded);
  bundleItemAddedRef.current = bundleItemAdded;

  /* when used for bundle builder, clicking the Load More btn will set this to true and 
  trigger loading the rest of the products into the product grid */
  const [bundleBuilderLoadMoreEnabled, setbundleBuilderLoadMoreEnabled] = useState(false);

  // required amount of bundle builder items to achieve promo
  const requiredCount = props.collection.requiredSelectedProductCount;
  // the step on the bundle builder page - e.g. category 1
  const category = props.bundleBuilderCategory;

  const availableBundleBuilderCategorySlots = requiredCount - bundleBuilderSelectedProducts.length;

  if (props.collection.useBuilderFunctionality) {
    const headerItems = document.querySelectorAll(`[data-header-category="${category}"]`);

    headerItems.forEach(item => {
      item.classList.remove("header-item-selected");
      // eslint-disable-next-line no-param-reassign
      item.querySelector('.header-item-product-title').innerText = "";
      item.setAttribute('data-header-item-pid', 0);
    })

    if (bundleBuilderSelectedProducts.length) {
      bundleBuilderSelectedProducts.forEach((product, index) => {
        const categoryHeaderItemString = `[data-header-category="${category}"][data-item="${index + 1}"]`;
        const categoryHeaderItem = document.querySelector(categoryHeaderItemString);
        categoryHeaderItem.querySelector('.header-item-product-title').innerText = product.title;
        categoryHeaderItem.classList.add("header-item-selected");
        categoryHeaderItem.setAttribute('data-header-item-pid', product.id);
      })
    }
  }

  const getCollection = async () => {
    const response = await storefront.request({
      query: queries.collectionGet,
      variables: {
        id: props.collection.id,
        first: 50,
      },
    });

    if (!response.data) {
      // eslint-disable-next-line no-console
      console.error(
        'product-grid/product-grid.js: Storefront API request failed',
        response,
      );
      return [];
    }

    return edgesToNodes(response.data.collection?.products, 'product');
  };

  const handleBpChange = () => {
    setIsDesktop(props.layout.bps.above.lg.matches);
  };

  useEffect(async () => {
    if (products.length) return;
    setProducts(await getCollection());
    window.addEventListener('bpChange', handleBpChange);
  }, []);

  const handleLoadMoreClick = () => {
    setBundleItemAdded({id: 0, multi: null});
    setBundleItemRemoved({id: 0, multi: null});
    setTimeout(() => {
      setbundleBuilderLoadMoreEnabled(true);
    }, 100);
  };

  const addToBundleBuilder = (productTitle, productId, quantity, firstVariantId, collection, uid) => {
    if (requiredCount === bundleBuilderSelectedProducts.length) return;

    // prevent over-adding of bundle items
    const loopCount = quantity > availableBundleBuilderCategorySlots ? availableBundleBuilderCategorySlots : quantity;

    // init array that will be used to update bundle builder state
    const prodArray = [];

    // multi indicates that the same product has been added multiple times to the bundle
    const obj = {
      title: productTitle,
      id: productId,
      multi: loopCount > 1,
      qty: loopCount,
      collection,
      uid,
      firstVariantId
    }

    for (let index = 0; index < loopCount; index += 1) {
      prodArray.push(obj);
    }

    setBundleBuilderSelectedProducts(currentState => [...currentState, ...prodArray]);
    setBundleItemAdded(obj);
    setBundleItemRemoved({id: 0, multi: null});
  }

  const removeFromBundleBuilder = (event) => {
    // get product ID for the one that needs to be removed
    const {headerItemPid} = event.target.closest('.bundle-builder-page-slide').dataset;

    let itemRemovedArray;
    let hasMulti;

    // ultimately, we only want to remove one but the same product might exist multiple times so we need to check for that
    if (bundleBuilderSelectedProductsRef.current.some(item => item.multi === true)) {
      hasMulti = true;
      const multiItems = bundleBuilderSelectedProductsRef.current.filter(p => p.multi === true);
      const nonMultiItems = bundleBuilderSelectedProductsRef.current.filter(p => p.multi === false);

      if (multiItems.some(i => i.id === headerItemPid)) {
        const itemsReduced = multiItems.slice(0, -1);
        setBundleBuilderSelectedProducts([...nonMultiItems, ...itemsReduced]);
      }
      else {
        itemRemovedArray = bundleBuilderSelectedProductsRef.current.filter(item => item.id !== headerItemPid);
        setBundleBuilderSelectedProducts(itemRemovedArray);
      }
    }
    else {
      hasMulti = false;
      itemRemovedArray = bundleBuilderSelectedProductsRef.current.filter(item => item.id !== headerItemPid);
      setBundleBuilderSelectedProducts(itemRemovedArray);
    }

    setBundleItemRemoved({id: parseInt(headerItemPid, 10), multi: hasMulti});
    setBundleItemAdded({id: 0, multi: null});
  }

  const bundleBuilderHeaderRemoveBtns = document.querySelectorAll(`[data-remove-btn-category="${category}"]`);

  bundleBuilderHeaderRemoveBtns.forEach((btn) => {
    if (btn.dataset.btnInitialized === 'false') {
      btn.addEventListener('click', removeFromBundleBuilder);

      // this is for preventing a bug that was attaching multiple click events to a single button
      btn.setAttribute('data-btn-initialized', 'true');
    }
  })

  const bundleBuilderClearAllItemsBtn = document.querySelector('[data-clear-all-bundle-items]');
  bundleBuilderClearAllItemsBtn.addEventListener('click', () => {
    props.updateTotalCount("clear");
    setBundleItemAdded({id: 0, multi: null});
    setBundleItemRemoved({id: 0, multi: null});
    setBundleBuilderSelectedProducts([]);
  })

  return (
    !!products.length && (
      <div
        className="grid grid-cols-2 gap-3 md:gap-6 md:grid-cols-4"
        data-grid-category={props.bundleBuilder ? category : null}
        data-category-selected-items={bundleBuilderSelectedProducts.length}
      >
        {/* when not used for a bundler builder then output the grid as usual */}
        {!props.bundleBuilder &&
          products.map((product, index) => (
            <ProductCard
              key={product.id}
              product={product}
              useExtendedFunctionality={props.collection.useExtendedFunctionality}
              useBuilderFunctionality={props.collection.useBuilderFunctionality}
              showMobileAltImage={true}
              isDesktop={isDesktop}
              index={index}
              {...props}
            />
          ))
        }

        {/* when used for bundle builder, only output 12 products on page load */}
        {props.bundleBuilder && !bundleBuilderLoadMoreEnabled &&
          products.slice(0, 12).map((product, index) => (
            <ProductCard
              key={product.id}
              product={product}
              useExtendedFunctionality={props.collection.useExtendedFunctionality}
              useBuilderFunctionality={props.collection.useBuilderFunctionality}
              showMobileAltImage={true}
              isDesktop={isDesktop}
              index={index}
              {...props}
              addToBundleBuilder={addToBundleBuilder}
              requiredCount={requiredCount}
              availableBundleBuilderCategorySlots={availableBundleBuilderCategorySlots}
              bundleBuilderSelectedProducts={bundleBuilderSelectedProducts}
              bundleItemRemoved={bundleItemRemoved}
              setBundleItemRemoved={setBundleItemRemoved}
              bundleItemAdded={bundleItemAdded}
              setBundleItemAdded={setBundleItemAdded}
            />
          ))
        }

        {/* when used for bundle builder, output remaining products when Load More btn clicked */}
        {props.bundleBuilder && bundleBuilderLoadMoreEnabled &&
          products.map((product, index) => (
            <ProductCard
              key={product.id}
              product={product}
              useExtendedFunctionality={props.collection.useExtendedFunctionality}
              useBuilderFunctionality={props.collection.useBuilderFunctionality}
              showMobileAltImage={true}
              isDesktop={isDesktop}
              index={index}
              {...props}
              addToBundleBuilder={addToBundleBuilder}
              requiredCount={requiredCount}
              availableBundleBuilderCategorySlots={availableBundleBuilderCategorySlots}
              bundleBuilderSelectedProducts={bundleBuilderSelectedProducts}
              bundleItemRemoved={bundleItemRemoved}
              setBundleItemRemoved={setBundleItemRemoved}
              bundleItemAdded={bundleItemAdded}
              setBundleItemAdded={setBundleItemAdded}
            />
          ))
        }

        {props.bundleBuilder && products.length > 12 &&
          <div className={`col-span-full lg:col-start-2 lg:col-end-4 w-fit mx-auto ${bundleBuilderLoadMoreEnabled ? 'invisible' : 'mt-8'}`}>
            <p className='text-center mb-3'>Showing 12 of {products.length} Products</p>

            <button
              type='button'
              className='button button--primary w-72'
              data-bundle-builder-load-more-btn={category}
              onClick={handleLoadMoreClick}
            >
              Load More
            </button>
          </div>
        }
      </div>
    )
  );
};

export default ProductGrid;
